import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../context/AuthContext';
import {
  getAssessAllActionsOptimized,
  saveAssessEvaluation,
  uploadAssessPhoto,
  deleteAssessPhoto,
  deleteAssessEvaluation,
  toggleAssessMatrixCheck,
  getSafetyBasicInfo,
  patchAssessEvaluationAction,
  getSafetyProjects,
  getSafetyProjectYears
} from '../../../api/safetyAssessApi';
import RiskFormModal from './steps/RiskFormModal';
import RiskViewModal from './steps/RiskViewModal';
import {
  PlayCircleOutline as PlayIcon,
  CheckCircleOutline as CheckIcon,
  CancelOutlined as CancelIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';

function SafetyActionList() {
  const { token, user } = useContext(AuthContext);

  // 모드 제어 및 필터 상태
  const [viewMode, setViewMode] = useState('INIT'); // 'INIT', 'ALL': 전체 개선 조치, 'MY': 나의 개선 조치
  const [filterStatus, setFilterStatus] = useState('ALL'); // 'ALL', '완료', '진행중', '미완료' (조치사항 상태)
  const [projectStatusFilter, setProjectStatusFilter] = useState('진행중'); // 프로젝트 상태 필터 탭
  const [loading, setLoading] = useState(true);

  // 프로젝트 필터 상태
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear().toString());
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('ALL');
  const [projectsList, setProjectsList] = useState([]);

  // 데이터 상태
  const [actionItems, setActionItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, completed: 0, inProgress: 0, incompletePlan: 0, weeklyNew: 0, onTimeRate: 100, overdue: 0, highRiskNoPlan: 0 });

  // 페이징
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [evalMethod, setEvalMethod] = useState('3X3');
  const [siteManagerId, setSiteManagerId] = useState('');

  // 상세 모달 및 사진 모달 관련 상태
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  // 로컬 사진 및 프리뷰 임시 제어 상태
  const [tempBeforeFile, setTempBeforeFile] = useState(null);
  const [tempAfterFile, setTempAfterFile] = useState(null);
  const [tempBeforeUrl, setTempBeforeUrl] = useState('');
  const [tempAfterUrl, setTempAfterUrl] = useState('');
  const [beforePhotoId, setBeforePhotoId] = useState(null);
  const [afterPhotoId, setAfterPhotoId] = useState(null);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState([]);

  // 개선 대책 직접 등록 모달
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editPlanText, setEditPlanText] = useState('');

  // 위험성 감소 변화(개선전/개선후 점수) 인라인 팝오버 상태
  const [popoverActionId, setPopoverActionId] = useState(null);
  const [popoverType, setPopoverType] = useState(null); // 'BEFORE' | 'AFTER'
  const [tempFreq, setTempFreq] = useState(1);
  const [tempSev, setTempSev] = useState(1);


  // 시스템 최고 관리자 권한 판별
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN' || user?.role === 'admin' || user?.userid === 'admin';

  // 초기 viewMode 세팅 (나의 조치 대상 유무 확인 - 전체 상태 기준)
  useEffect(() => {
    const initView = async () => {
      if (viewMode !== 'INIT') return;
      if (!token || !user?.userid) return;

      try {
        const myParams = {
          limit: 1,
          status: 'ALL',
          assigneeUserid: user.userid
        };
        const res = await getAssessAllActionsOptimized(token, myParams);
        if (res && res.totalCount > 0) {
          setViewMode('MY');
        } else {
          setViewMode('ALL');
        }
      } catch (err) {
        setViewMode('ALL');
      }
    };
    initView();
  }, [viewMode, token, user]);

  // 팝오버 외부 클릭 감지하여 자동 닫기
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (popoverActionId && !e.target.closest('.risk-popover-box') && !e.target.closest('.risk-badge-btn')) {
        setPopoverActionId(null);
        setPopoverType(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [popoverActionId]);

  // DB에 존재하는 프로젝트 연도 목록 조회
  useEffect(() => {
    const fetchYears = async () => {
      if (!token) return;
      try {
        const years = await getSafetyProjectYears(token);
        if (years && years.length > 0) {
          setAvailableYears(years);
          const currentYear = new Date().getFullYear().toString();
          if (!years.includes(parseInt(currentYear, 10)) && !years.includes(currentYear)) {
            setFilterYear(years[0].toString());
          }
        }
      } catch (err) {
        console.error('fetchYears error:', err);
      }
    };
    fetchYears();
  }, [token]);

  // 통계 계산 및 서버 API 데이터 조회
  const fetchActions = useCallback(async (silent = false) => {
    if (!token) return;
    if (viewMode === 'INIT') return; // 초기 모드 결정 대기
    try {
      if (silent !== true) setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        status: filterStatus === '완료' ? 'completed' : filterStatus === '진행중' ? 'in_progress' : filterStatus === '미완료' ? 'pending' : 'ALL'
      };

      // 내 담당 조치만 보기 필터링 적용 (오직 담당자 여부만 판별)
      if (viewMode === 'MY') {
        params.assigneeUserid = user?.userid;
      }

      if (selectedProjectId && selectedProjectId !== 'ALL') {
        params.assessmentId = selectedProjectId;
      }

      if (filterYear && filterYear !== 'ALL') {
        params.year = filterYear;
      }

      if (projectStatusFilter) {
        params.projectStatus = projectStatusFilter;
      }

      const result = await getAssessAllActionsOptimized(token, params);
      if (result && result.items) {
        // 결과 및 백엔드 사전집계 통계 바인딩
        setActionItems(result.items);
        setTotalCount(result.totalCount);

        if (result.stats) {
          setStats(result.stats);
        }
      }
    } catch (err) {
      console.error('fetchActions error:', err);
    } finally {
      if (silent !== true) setLoading(false);
    }
  }, [token, currentPage, filterStatus, viewMode, user, selectedProjectId, filterYear, projectStatusFilter]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // 프로젝트 목록 가져오기
  useEffect(() => {
    const fetchProjectsList = async () => {
      if (!token) return;
      try {
        const params = {};
        if (filterYear && filterYear !== 'ALL') {
          params.year = filterYear;
        }
        if (projectStatusFilter) {
          params.projectStatus = projectStatusFilter;
        }
        const result = await getSafetyProjects(token, params);
        if (result && result.data) {
          setProjectsList(result.data);
        } else if (Array.isArray(result)) {
          setProjectsList(result);
        }
      } catch (err) {
        console.error('getSafetyProjects error:', err);
      }
    };
    fetchProjectsList();
  }, [token, filterYear, projectStatusFilter]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const basicInfo = await getSafetyBasicInfo(token);
        if (basicInfo) {
          if (basicInfo.evalMethod) setEvalMethod(basicInfo.evalMethod);
          if (basicInfo.managerId) setSiteManagerId(basicInfo.managerId);
        }
      } catch (err) {
        console.error('설정 조회 실패:', err);
      }
    };
    fetchConfig();
  }, [token]);

  // 위험성 점수 등급 판정 유틸리티 (단일 점수 directScore 호환)
  const getRiskLabel = (freq, sev, method, directScore) => {
    let s = directScore ? parseInt(directScore, 10) : (parseInt(freq, 10) * parseInt(sev, 10));
    if (!s || isNaN(s)) return { color: '#e2e8f0', text: '#64748b', label: '-' };
    if (method === '3X3') {
      if (s === 1 || s === 2) return { color: '#10b981', text: '#ffffff', label: `${s}점 (하)` };
      if (s === 3 || s === 4) return { color: '#f59e0b', text: '#ffffff', label: `${s}점 (중)` };
      return { color: '#ef4444', text: '#ffffff', label: `${s}점 (상)` };
    } else {
      if (s <= 4) return { color: '#3b82f6', text: '#ffffff', label: `${s}점 (낮음)` };
      if (s <= 8) return { color: '#10b981', text: '#ffffff', label: `${s}점 (보통)` };
      if (s <= 16) return { color: '#f59e0b', text: '#ffffff', label: `${s}점 (높음)` };
      return { color: '#ef4444', text: '#ffffff', label: `${s}점 (치명적)` };
    }
  };

  // 인라인 수정 권한 체크 (작성자, 담당자, 관리자, 사업장 안전관리자)
  const checkCanEdit = (ev) => {
    if (!user) return false;
    const currentUserId = user.userid || user.userId;
    if (!currentUserId) return false;
    
    // 1. 최고 관리자 (admin) 프리패스
    if (isAdmin) return true;
    
    // 2. 안전 관리자
    if (siteManagerId && currentUserId === siteManagerId) return true;
    
    // 3. 기안자(작성자)
    if (ev.writeId && ev.writeId === currentUserId) return true;
 
    // 4. 조치 담당자 (사번, 아이디, 또는 이름 일치)
    if (ev.assignees) {
      let assigneesList = [];
      try {
        assigneesList = typeof ev.assignees === 'string' ? JSON.parse(ev.assignees) : ev.assignees;
      } catch (e) { }
      // 사번, 아이디, 혹은 이름이 일치하는지 종합 체크
      if (assigneesList.some(a => 
        (a.emp_no && a.emp_no === currentUserId) || 
        (a.userid && a.userid === currentUserId) ||
        (a.name && (a.name === user.name || user.name?.includes(a.name) || a.name?.includes(user.name)))
      )) return true;
    }
    return false;
  };

  const handleMeasureSave = async (item, newText) => {
    if (!newText || !newText.trim()) {
      alert('유해위험요인을 입력해 주세요.');
      return;
    }
    try {
      let finalEv = item.evaluation;
      if (!finalEv || !finalEv.id) {
        finalEv = await getOrCreateEvaluation(item);
      }

      await patchAssessEvaluationAction(finalEv.id, { currentMeasure: newText }, token);

      setActionItems(prevItems => prevItems.map(prevItem => {
        if (prevItem.matrixCheckId === item.matrixCheckId) {
          return {
            ...prevItem,
            evaluation: {
              ...finalEv,
              currentMeasure: newText
            }
          };
        }
        return prevItem;
      }));
    } catch (err) {
      console.error(err);
      alert('저장에 실패했습니다.');
    }
  };

  const handleActionSave = async (item, newText) => {
    if (!newText || !newText.trim()) {
      alert('개선대책을 입력해 주세요.');
      return;
    }
    try {
      let finalEv = item.evaluation;
      if (!finalEv || !finalEv.id) {
        finalEv = await getOrCreateEvaluation(item);
      }

      await patchAssessEvaluationAction(finalEv.id, { action: newText }, token);

      setActionItems(prevItems => prevItems.map(prevItem => {
        if (prevItem.matrixCheckId === item.matrixCheckId) {
          return {
            ...prevItem,
            evaluation: {
              ...finalEv,
              action: newText,
              actionStatus: newText ? (finalEv.actionStatus === 'pending' ? 'in_progress' : finalEv.actionStatus) : 'pending'
            }
          };
        }
        return prevItem;
      }));
    } catch (err) {
      console.error(err);
      alert('저장에 실패했습니다.');
    }
  };

  // 평가정보 뼈대 생성 또는 ID 조회
  const getOrCreateEvaluation = async (item) => {
    if (item.evaluation && item.evaluation.id) {
      return item.evaluation;
    }
    // 데이터가 아예 없는 경우 skeleton 생성
    const payload = {
      matrixCheckId: item.matrixCheckId,
      currentMeasure: item.memo,
      action: '',
      freqBefore: 2,
      sevBefore: 2,
      actionStatus: 'pending'
    };
    return await saveAssessEvaluation(payload, token);
  };

  // 진행중 처리 상태 변경
  const handleStartProgress = async (item) => {
    const ev = item.evaluation || {};
    if (!checkCanEdit(ev)) {
      alert('진행중 처리 권한이 없습니다. (시스템 관리자, 안전관리자, 작성자, 담당자만 가능)');
      return;
    }
    if (!window.confirm('이 항목을 진행중 상태로 변경하시겠습니까?')) return;
    try {
      let finalEv = item.evaluation;
      if (!finalEv || !finalEv.id) {
        finalEv = await getOrCreateEvaluation(item);
      }
      const payload = {
        id: finalEv.id,
        matrix_check_id: item.matrixCheckId,
        currentMeasure: finalEv.currentMeasure || item.memo || '',
        action: finalEv.action || '',
        freqBefore: finalEv.freqBefore || 2,
        sevBefore: finalEv.sevBefore || 2,
        actionStatus: 'in_progress'
      };
      await saveAssessEvaluation(payload, token);
      alert('진행중 상태로 변경되었습니다.');
      fetchActions();
    } catch (e) {
      console.error(e);
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  // 완료 승인 처리
  const handleApprove = async (item) => {
    const ev = item.evaluation;
    if (!checkCanEdit(ev)) {
      alert('최종 완료 승인 권한이 없습니다. (시스템 관리자, 안전관리자, 작성자, 담당자만 가능)');
      return;
    }
    
    // 1. 유해위험요인 파악 (현상) 입력 여부 체크
    if (!ev || !ev.currentMeasure || !ev.currentMeasure.trim() || ev.currentMeasure.trim() === '-') {
      alert('유해위험요인 파악 내용을 먼저 등록해야 승인할 수 있습니다.');
      return;
    }

    // 2. 개선대책 내용 체크
    if (!ev.action || !ev.action.trim()) {
      alert('개선대책 내용을 입력해야 승인할 수 있습니다.');
      return;
    }
    
    // 1. 담당 부서/담당자 지정 여부 체크
    if (!ev.assignees || ev.assignees.length === 0) {
      alert('조치 담당자(또는 부서)를 지정해야 승인할 수 있습니다.');
      return;
    }

    // 2. 개선 후 위험도 입력 여부 체크
    if (!ev.freqAfter || !ev.sevAfter) {
      alert('개선 후 위험성(빈도/강도) 평가 점수를 입력해야 승인할 수 있습니다.');
      return;
    }

    let confirmMessage = '이 개선 조치 이행을 최종 완료 승인하시겠습니까?';
    let finalDoneDate = ev.doneDate;
    
    if (!ev.doneDate) {
      confirmMessage = '조치완료일이 입력되지 않았습니다. 오늘 날짜로 지정하여 완료 처리하시겠습니까?';
      finalDoneDate = new Date().toISOString().split('T')[0];
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const payload = {
        id: ev.id,
        matrix_check_id: item.matrixCheckId,
        currentMeasure: ev.currentMeasure,
        action: ev.action,
        freqBefore: ev.freqBefore,
        sevBefore: ev.sevBefore,
        freqAfter: ev.freqAfter,
        sevAfter: ev.sevAfter,
        assignees: ev.assignees,
        deptName: ev.deptName,
        dueDate: ev.dueDate,
        doneDate: finalDoneDate,
        actionStatus: 'completed'
      };

      await saveAssessEvaluation(payload, token);
      alert('이행 조치 승인이 완료되었습니다.');
      fetchActions(true);
    } catch (e) {
      console.error(e);
      alert('승인 처리 중 오류가 발생했습니다.');
    }
  };

  // 개선 전 위험도 자동(인라인) 저장 (빈도/강도 매트릭스 처리)
  const handleAutoSaveBeforeRisk = async (item, freq, sev) => {
    try {
      const ev = await getOrCreateEvaluation(item);
      const fBefore = parseInt(freq, 10);
      const sBefore = parseInt(sev, 10);
      const fAfter = ev.freqAfter ? parseInt(ev.freqAfter, 10) : null;
      const sAfter = ev.sevAfter ? parseInt(ev.sevAfter, 10) : null;

      const payload = {
        id: ev.id,
        matrixCheckId: item.matrixCheckId,
        matrix_check_id: item.matrixCheckId,
        currentMeasure: ev.currentMeasure || item.memo || '',
        action: ev.action || '',
        freqBefore: fBefore,
        sevBefore: sBefore,
        riskBefore: fBefore * sBefore,
        freqAfter: fAfter,
        sevAfter: sAfter,
        riskAfter: (fAfter && sAfter) ? (fAfter * sAfter) : null,
        assignees: ev.assignees,
        deptName: ev.deptName,
        dueDate: ev.dueDate,
        actionStatus: ev.actionStatus || 'pending'
      };
      await saveAssessEvaluation(payload, token);
      setPopoverActionId(null);
      setPopoverType(null);

      // 스크롤 튀지 않고 해당 항목 뱃지만 실시간 갱신
      setActionItems(prevItems => prevItems.map(pi => {
        if (pi.matrixCheckId === item.matrixCheckId) {
          return {
            ...pi,
            evaluation: {
              ...(pi.evaluation || {}),
              id: ev.id,
              freqBefore: fBefore,
              sevBefore: sBefore,
              riskBefore: fBefore * sBefore,
              freqAfter: fAfter,
              sevAfter: sAfter,
              riskAfter: (fAfter && sAfter) ? (fAfter * sAfter) : null
            }
          };
        }
        return pi;
      }));
    } catch (e) {
      console.error(e);
      alert('위험도 업데이트에 실패했습니다.');
    }
  };

  // 개선 후 위험도 자동(인라인) 저장 (빈도/강도 매트릭스 처리)
  const handleAutoSaveAfterRisk = async (item, freq, sev) => {
    try {
      const ev = await getOrCreateEvaluation(item);
      const fBefore = ev.freqBefore ? parseInt(ev.freqBefore, 10) : 2;
      const sBefore = ev.sevBefore ? parseInt(ev.sevBefore, 10) : 2;
      const fAfter = parseInt(freq, 10);
      const sAfter = parseInt(sev, 10);

      const payload = {
        id: ev.id,
        matrixCheckId: item.matrixCheckId,
        matrix_check_id: item.matrixCheckId,
        currentMeasure: ev.currentMeasure || item.memo || '',
        action: ev.action || '',
        freqBefore: fBefore,
        sevBefore: sBefore,
        riskBefore: fBefore * sBefore,
        freqAfter: fAfter,
        sevAfter: sAfter,
        riskAfter: fAfter * sAfter,
        assignees: ev.assignees,
        deptName: ev.deptName,
        dueDate: ev.dueDate,
        actionStatus: ev.actionStatus || 'pending'
      };
      await saveAssessEvaluation(payload, token);
      setPopoverActionId(null);
      setPopoverType(null);

      // 스크롤 튀지 않고 해당 항목 뱃지만 실시간 갱신
      setActionItems(prevItems => prevItems.map(pi => {
        if (pi.matrixCheckId === item.matrixCheckId) {
          return {
            ...pi,
            evaluation: {
              ...(pi.evaluation || {}),
              id: ev.id,
              freqBefore: fBefore,
              sevBefore: sBefore,
              riskBefore: fBefore * sBefore,
              freqAfter: fAfter,
              sevAfter: sAfter,
              riskAfter: fAfter * sAfter
            }
          };
        }
        return pi;
      }));
    } catch (e) {
      console.error(e);
      alert('위험도 업데이트에 실패했습니다.');
    }
  };

  // 개선 대책 수립/저장
  const handleSavePlan = async () => {
    if (!editPlanText.trim()) return alert('개선 대책 내용을 입력해 주세요.');
    try {
      const ev = await getOrCreateEvaluation(selectedItem);
      const payload = {
        id: ev.id,
        matrix_check_id: selectedItem.matrixCheckId,
        currentMeasure: ev.currentMeasure || selectedItem.memo,
        action: editPlanText,
        freqBefore: ev.freqBefore || 2,
        sevBefore: ev.sevBefore || 2,
        freqAfter: ev.freqBefore || 2,
        sevAfter: Math.max(1, (ev.sevBefore || 2) - 1),
        assignees: ev.assignees || [],
        deptName: ev.deptName || '',
        actionStatus: 'in_progress' // 대책 수립 시 '진행중'으로 격상
      };

      await saveAssessEvaluation(payload, token);
      setShowEditPlanModal(false);
      setSelectedItem(null);
      alert('개선 대책이 성공적으로 등록되었습니다.');
      fetchActions();
    } catch (e) {
      console.error(e);
      alert('개선 대책 등록 실패');
    }
  };

  // 로컬 사진 파일 선택 처리 (프리뷰 활성화)
  const handlePhotoSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === 'BEFORE') {
      setTempBeforeFile(file);
      setTempBeforeUrl(url);
    } else {
      setTempAfterFile(file);
      setTempAfterUrl(url);
    }
  };

  // 로컬 사진 삭제 예약 처리
  const handlePhotoRemoveLocal = (type) => {
    if (type === 'BEFORE') {
      if (beforePhotoId) {
        setDeletedPhotoIds(prev => [...prev, beforePhotoId]);
        setBeforePhotoId(null);
      }
      setTempBeforeFile(null);
      setTempBeforeUrl('');
    } else {
      if (afterPhotoId) {
        setDeletedPhotoIds(prev => [...prev, afterPhotoId]);
        setAfterPhotoId(null);
      }
      setTempAfterFile(null);
      setTempAfterUrl('');
    }
  };

  // 사진 저장 일괄 전송 처리
  const handlePhotoSaveAll = async () => {
    try {
      setUploading(true);
      const ev = await getOrCreateEvaluation(photoTarget);

      // 1. 삭제 예약된 사진 삭제 API 호출
      for (const id of deletedPhotoIds) {
        await deleteAssessPhoto(id, token);
      }

      // 2. 새로 선택된 Before 사진 업로드
      if (tempBeforeFile) {
        await uploadAssessPhoto(ev.id, 'BEFORE', tempBeforeFile, token);
      }

      // 3. 새로 선택된 After 사진 업로드
      if (tempAfterFile) {
        await uploadAssessPhoto(ev.id, 'AFTER', tempAfterFile, token);
      }

      alert('사진 정보가 성공적으로 저장되었습니다.');
      setShowPhotoModal(false);
      setPhotoTarget(null);
      setTempBeforeFile(null);
      setTempAfterFile(null);
      setDeletedPhotoIds([]);
      fetchActions();
    } catch (err) {
      console.error(err);
      alert('사진 저장 실패');
    } finally {
      setUploading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  return (
    <div className="sf-page sf-animate" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', background: '#ffffff', paddingBottom: '20px' }}>
      <style>{`
        .hero-banner { background: linear-gradient(to right, #0f172a 0%, #1e293b 25%, #64748b 100%); border-radius: 12px; padding: 1.5rem 2rem 2.25rem 2rem; color: white; display: flex; justify-content: space-between; align-items: center; position: relative; overflow: hidden; }
        .kpi-wrapper { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-top: -1.25rem; padding: 0 1.5rem; position: relative; z-index: 10; }
        .kpi-box { background: white; border-radius: 12px; padding: 0.85rem 1.25rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid rgba(226, 232, 240, 0.8); }
        .hero-banner p { margin: 0; color: #94a3b8; font-size: 0.85rem; }
        .action-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px 8px 16px; transition: all 0.25s ease; position: relative; }
        .action-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.06); border-color: #cbd5e1; }
      `}</style>

      {/* 헤더 배너 */}
      <div className="hero-banner">
        <div style={{ flex: 1, minWidth: 0, paddingRight: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ background: '#334155', color: '#e2e8f0', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600 }}>통합 안전관리</span>
              <div style={{ background: '#dbeafe', color: '#1d4ed8', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 800 }}>
                👤 로그인 정보: {user?.name || '사용자'}({user?.position || '직급'})
              </div>
            </div>
            {/* 프로젝트 상태 필터 탭 (6안: 글래스모피즘 + 세련된 MUI 벡터 아이콘) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(15, 23, 42, 0.55)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              padding: '5px 6px',
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)'
            }}>
              {[
                { key: '진행중', icon: <PlayIcon style={{ fontSize: '1.1rem' }} />, activeBg: 'linear-gradient(135deg, #2563eb, #1d4ed8)', glow: '0 4px 14px rgba(37, 99, 235, 0.45)', border: '#60a5fa' },
                { key: '완료', icon: <CheckIcon style={{ fontSize: '1.1rem' }} />, activeBg: 'linear-gradient(135deg, #059669, #047857)', glow: '0 4px 14px rgba(5, 150, 105, 0.45)', border: '#34d399' },
                { key: '취소', icon: <CancelIcon style={{ fontSize: '1.1rem' }} />, activeBg: 'linear-gradient(135deg, #64748b, #475569)', glow: '0 4px 14px rgba(100, 116, 139, 0.45)', border: '#94a3b8' },
                ...(isAdmin ? [{ key: '삭제', icon: <DeleteIcon style={{ fontSize: '1.1rem' }} />, activeBg: 'linear-gradient(135deg, #dc2626, #b91c1c)', glow: '0 4px 14px rgba(220, 38, 38, 0.45)', border: '#f87171' }] : [])
              ].map(tab => {
                const isActive = projectStatusFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setProjectStatusFilter(tab.key);
                      setSelectedProjectId('ALL');
                      setCurrentPage(1);
                    }}
                    style={{
                      background: isActive ? tab.activeBg : 'rgba(255, 255, 255, 0.04)',
                      color: isActive ? '#ffffff' : '#cbd5e1',
                      border: isActive ? `1px solid ${tab.border}` : '1px solid transparent',
                      padding: '7px 20px',
                      borderRadius: '10px',
                      fontSize: '0.92rem',
                      fontWeight: isActive ? 800 : 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isActive ? tab.glow : 'none',
                      letterSpacing: '-0.3px',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', opacity: isActive ? 1 : 0.75 }}>{tab.icon}</span>
                    <span>{tab.key}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: 'white', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
            나의 위험성평가 개선조치 관리
          </h3>
          <p>발굴된 유해·위험요인에 대한 시정 조치 명령 및 개선 대책 이행 현황을 모니터링합니다.</p>
        </div>
      </div>

      {/* KPI 통계 카드 (필터 탭 겸용) */}
      <div className="kpi-wrapper mb-4">
        {[
          { key: 'ALL', label: '총 개선 요구 항목', value: stats.total, unit: '건', color: '#3b82f6', activeBg: '#eff6ff', textColor: '#0f172a', badge: '전체', badgeColor: '#dbeafe', badgeText: '#1d4ed8', subLabel: stats.weeklyNew ? `최근 7일간 +${stats.weeklyNew}건 추가` : '최근 신규 추가 없음' },
          { key: '완료', label: '조치 완료', value: stats.completed, unit: `건 (${stats.total ? Math.round((stats.completed / stats.total) * 100) : 0}%)`, color: '#10b981', activeBg: '#f0fdf4', textColor: '#10b981', badge: '양호', badgeColor: '#d1fae5', badgeText: '#047857', subLabel: `기한 준수율 ${stats.onTimeRate || 100}%` },
          { key: '진행중', label: '조치 진행 중', value: stats.inProgress, unit: '건', color: '#f59e0b', activeBg: '#fffbeb', textColor: '#f59e0b', badge: '모니터링', badgeColor: '#fef3c7', badgeText: '#b45309', subLabel: stats.overdue ? `⚠️ 예정일 지남 ${stats.overdue}건` : '예정일 지연 없음' },
          { key: '미완료', label: '미완료 (대기)', value: (stats.pending !== undefined ? stats.pending : (stats.total - stats.completed - stats.inProgress)), unit: '건', color: '#64748b', activeBg: '#f1f5f9', textColor: '#475569', badge: '조치 대기', badgeColor: '#e2e8f0', badgeText: '#334155', subLabel: stats.highRiskNoPlan ? `🚨 상 위험도 미수립 ${stats.highRiskNoPlan}건` : '대기 항목 관리' },
        ].map((kpi, idx) => {
          const isActive = filterStatus === kpi.key;
          return (
            <div
              className="kpi-box"
              key={idx}
              onClick={() => { setFilterStatus(kpi.key); setCurrentPage(1); }}
              style={{
                borderTop: `4px solid ${kpi.color}`,
                borderRight: isActive ? `2px solid ${kpi.color}` : '1px solid rgba(226, 232, 240, 0.8)',
                borderBottom: isActive ? `2px solid ${kpi.color}` : '1px solid rgba(226, 232, 240, 0.8)',
                borderLeft: isActive ? `2px solid ${kpi.color}` : '1px solid rgba(226, 232, 240, 0.8)',
                cursor: 'pointer',
                background: isActive ? kpi.activeBg : 'white',
                boxShadow: isActive ? '0 8px 16px rgba(0,0,0,0.08)' : '0 4px 6px -1px rgba(0,0,0,0.05)',
                transform: isActive ? 'translateY(-2px)' : 'none',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              {/* 상단 라인: 카테고리명 & 상태 배지 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#64748b', fontWeight: 600, fontSize: '0.82rem' }}>{kpi.label}</span>
                <span style={{ background: kpi.badgeColor, color: kpi.badgeText, padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700 }}>
                  {kpi.badge}
                </span>
              </div>
              
              {/* 하단 라인: 건수 & 실데이터 지표 (우측 정렬) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '4px' }}>
                <h3 style={{ fontWeight: 800, margin: 0, color: kpi.textColor, display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{ fontSize: '1.45rem' }}>{kpi.value}</span>
                  <span style={{ fontSize: '0.8rem', color: kpi.textColor }}>{kpi.unit}</span>
                </h3>
                <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
                  {kpi.subLabel}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 실 콘텐츠 영역 */}
      <div style={{ padding: '0 24px', marginTop: '10px' }}>
        {/* 통합 필터 바 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '16px', borderBottom: '2px solid #cbd5e1', gap: '16px', flexWrap: 'wrap' }}>
          {/* 좌측: 연도, 프로젝트 드롭다운 및 상태 드롭다운 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* 연도 선택 드롭다운 */}
            <select
              value={filterYear}
              onChange={(e) => { 
                setFilterYear(e.target.value); 
                setSelectedProjectId('ALL'); // 연도가 바뀌면 프로젝트 선택도 초기화
                setCurrentPage(1); 
                setActionItems([]); 
                setTotalCount(0); 
                setStats({ total: 0, completed: 0, inProgress: 0, incompletePlan: 0, weeklyNew: 0, onTimeRate: 100, overdue: 0, highRiskNoPlan: 0 });
                setLoading(true);
              }}
              style={{
                height: '38px',
                padding: '0 32px 0 12px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#475569',
                outline: 'none',
                backgroundColor: '#fff',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px top 50%',
                backgroundSize: '10px auto'
              }}
            >
              <option value="ALL">전체 연도</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>

            {/* 프로젝트 선택 드롭다운 */}
            <select
              value={selectedProjectId}
              onChange={(e) => { setSelectedProjectId(e.target.value); setCurrentPage(1); }}
              style={{
                background: '#ffffff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                padding: '7px 12px',
                fontWeight: 700,
                fontSize: '0.85rem',
                outline: 'none',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                width: '350px',
                maxWidth: '350px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              <option value="ALL">전체 위험성평가</option>
              {projectsList.map(proj => (
                <option key={proj.id} value={proj.id}>
                  {proj.name}
                </option>
              ))}
            </select>

            {/* 상태 선택 드롭다운 */}
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
              style={{
                background: '#ffffff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                padding: '7px 12px',
                fontWeight: 700,
                fontSize: '0.85rem',
                outline: 'none',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                width: 'auto',
                minWidth: '150px'
              }}
            >
              <option value="ALL">전체 ({stats.total}건)</option>
              <option value="완료">조치완료 ({stats.completed}건)</option>
              <option value="진행중">진행중 ({stats.inProgress}건)</option>
              <option value="미완료">미완료(대기) ({stats.pending !== undefined ? stats.pending : (stats.total - stats.completed - stats.inProgress)}건)</option>
            </select>
          </div>

          {/* 우측: 전체/내담당 버튼 */}
          <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '10px', gap: 4, border: '1px solid #e2e8f0' }}>
            <button
              onClick={() => { setViewMode('ALL'); setCurrentPage(1); }}
              style={{
                background: viewMode === 'ALL' ? '#ffffff' : 'transparent',
                border: 'none', borderRadius: '8px', padding: '6px 16px', fontWeight: viewMode === 'ALL' ? 800 : 700,
                boxShadow: viewMode === 'ALL' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer',
                fontSize: '0.85rem', color: viewMode === 'ALL' ? '#1e40af' : '#475569', transition: 'all 0.2s'
              }}
            >
              전체 개선 조치
            </button>
            <button
              onClick={() => { setViewMode('MY'); setCurrentPage(1); }}
              style={{
                background: viewMode === 'MY' ? '#ffffff' : 'transparent',
                border: 'none', borderRadius: '8px', padding: '6px 16px', fontWeight: viewMode === 'MY' ? 800 : 700,
                boxShadow: viewMode === 'MY' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer',
                fontSize: '0.85rem', color: viewMode === 'MY' ? '#1e40af' : '#475569', transition: 'all 0.2s'
              }}
            >
              나의 개선 조치
            </button>
          </div>
        </div>

        {/* 카드 그리드 피드 리스트 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <style>{`
            .premium-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .premium-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            .premium-scrollbar::-webkit-scrollbar-thumb {
              background-color: #cbd5e1;
              border-radius: 10px;
            }
            .premium-scrollbar::-webkit-scrollbar-thumb:hover {
              background-color: #94a3b8;
            }
          `}</style>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div className="sf-spinner" style={{ margin: '0 auto 12px auto' }}></div>
              <div style={{ color: '#64748b', fontSize: '0.88rem' }}>개선조치 리스트 로딩 중...</div>
            </div>
          ) : actionItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', border: '1px dashed #cbd5e1', borderRadius: '12px', background: '#f8fafc' }}>
              <div style={{ color: '#cbd5e1', fontSize: '2.5rem', marginBottom: '12px' }}><i className="fas fa-folder-open"></i></div>
              <div style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: 600 }}>해당 조건의 개선조치 항목이 존재하지 않습니다.</div>
            </div>
          ) : (
            actionItems.map(item => {
              const ev = item.evaluation || {};
              const fBefore = parseInt(ev.freqBefore || ev.freq_before || 0, 10);
              const sBefore = parseInt(ev.sevBefore || ev.sev_before || 0, 10);
              const beforeScore = (fBefore && sBefore) ? (fBefore * sBefore) : (ev.riskBefore || ev.risk_before || 0);

              const fAfter = parseInt(ev.freqAfter || ev.freq_after || 0, 10);
              const sAfter = parseInt(ev.sevAfter || ev.sev_after || 0, 10);
              const afterScore = (fAfter && sAfter) ? (fAfter * sAfter) : (ev.riskAfter || ev.risk_after || 0);

              const beforeRiskObj = getRiskLabel(ev.freqBefore || ev.freq_before, ev.sevBefore || ev.sev_before, evalMethod, beforeScore);
              const afterRiskObj = getRiskLabel(ev.freqAfter || ev.freq_after, ev.sevAfter || ev.sev_after, evalMethod, afterScore);
              const isActionPlanEmpty = !ev.action || !ev.action.trim();
              const rawMeasure = (ev.currentMeasure || item.memo || '').trim();
              const isMeasureEmpty = !rawMeasure || rawMeasure === '-';

              // 담당자 파싱
              let assigneesList = [];
              if (ev.assignees) {
                try {
                  assigneesList = typeof ev.assignees === 'string' ? JSON.parse(ev.assignees) : ev.assignees;
                } catch (e) { }
              }

              return (
                <div key={item.matrixCheckId} className="action-card" style={{ position: 'relative', zIndex: (popoverActionId === item.matrixCheckId) ? 99999 : 1 }}>
                  <div className="row align-items-stretch" style={{ display: 'flex', flexWrap: 'wrap' }}>

                    {/* 왼쪽 정보 컬럼 (80%) */}
                    <div style={{ flex: '1 1 70%', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '7px', paddingRight: '20px' }}>
                      {/* 프로젝트명 및 상태 뱃지 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', marginBottom: '0', borderBottom: '1px solid #e2e8f0' }}>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.4 }}>
                          <i className="far fa-building" style={{ color: '#64748b', marginRight: '6px' }}></i>
                          {item.assessmentName}
                        </h4>
                        <span style={{
                          background: ev.actionStatus === 'completed' ? '#d1fae5' : ev.actionStatus === 'in_progress' ? '#fef3c7' : '#f1f5f9',
                          color: ev.actionStatus === 'completed' ? '#065f46' : ev.actionStatus === 'in_progress' ? '#b45309' : '#64748b',
                          border: ev.actionStatus === 'completed' ? 'none' : ev.actionStatus === 'in_progress' ? 'none' : '1px solid #cbd5e1',
                          padding: '2px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 800,
                          display: 'flex', alignItems: 'center'
                        }}>
                          {ev.actionStatus === 'completed' ? (
                            <><i className="fas fa-check" style={{ marginRight: '4px' }}></i>조치완료</>
                          ) : ev.actionStatus === 'in_progress' ? (
                            <><i className="fas fa-hourglass-half" style={{ marginRight: '4px' }}></i>조치 진행중</>
                          ) : (
                            <><i className="fas fa-clock" style={{ marginRight: '4px' }}></i>미완료 (대기)</>
                          )}
                        </span>
                      </div>

                      {/* 카드 헤더 태그 (Breadcrumb Style) - [공정 > 대분류 > 원인] 계층 순서 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', color: '#475569', fontSize: '0.75rem', fontWeight: 700, marginBottom: '0' }}>
                        <span style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <i className="fas fa-layer-group" style={{ color: '#8b5cf6', marginRight: '4px' }}></i> {item.processName || '사업장 공통'}
                        </span>
                        <i className="fas fa-chevron-right" style={{ color: '#64748b', fontSize: '0.6rem' }}></i>
                        <span style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <i className="far fa-folder-open" style={{ color: '#3b82f6', marginRight: '4px' }}></i> {item.categoryName || '대분류 없음'}
                        </span>
                        <i className="fas fa-chevron-right" style={{ color: '#64748b', fontSize: '0.6rem' }}></i>
                        <span style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <i className="fas fa-exclamation-triangle" style={{ color: '#ef4444', marginRight: '4px' }}></i> {item.causeName}
                        </span>
                      </div>

                      {/* 좌우 반반 분할 레이아웃 (프리미엄 디자인 적용) */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                        {/* 1. 유해위험요인 파악 박스 (입력 지연 분리) */}
                        <InlineMeasureBox
                          item={item}
                          ev={ev}
                          isMeasureEmpty={isMeasureEmpty}
                          canEdit={checkCanEdit(ev)}
                          onSave={handleMeasureSave}
                        />

                        {/* 2. 개선대책 본문 박스 (입력 지연 분리) */}
                        <InlineActionBox
                          item={item}
                          ev={ev}
                          isActionPlanEmpty={isActionPlanEmpty}
                          canEdit={checkCanEdit(ev)}
                          onSave={handleActionSave}
                        />
                      </div>
                    </div>

                    {/* 오른쪽 작업 패널 컬럼 (30% - 세로 구분선 포함) */}
                    <div style={{
                      flex: '1 1 25%',
                      minWidth: '220px',
                      borderLeft: '1px solid #e2e8f0',
                      paddingLeft: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      gap: '5px'
                    }}>
                      {/* 일정 박스 (한줄 압축 박스) */}
                      <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', minHeight: '38px', fontSize: '0.72rem', color: '#475569' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <i className="far fa-calendar-alt" style={{ color: '#3b82f6', fontSize: '0.75rem' }}></i>
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '1px 5px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 800 }}>예정</span>
                            <span style={{ fontWeight: 700, color: '#334155' }}>{ev.dueDate ? ev.dueDate.split('T')[0] : '미정'}</span>
                          </div>
                          {ev.doneDate && (
                            <>
                              <span style={{ color: '#cbd5e1', margin: '0 4px' }}>|</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <i className="fas fa-calendar-check" style={{ color: '#10b981', fontSize: '0.75rem' }}></i>
                                <span style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '1px 5px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 800 }}>완료</span>
                                <span style={{ fontWeight: 700, color: '#10b981' }}>{ev.doneDate.split('T')[0]}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* 담당자 박스 (한줄 박스 테두리 + 상하스크롤 없애도록 높이 조절) */}
                      <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', minHeight: '62px', fontSize: '0.72rem', color: '#475569' }}>
                        <span style={{ background: '#64748b', color: '#ffffff', padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 800, marginRight: '8px', whiteSpace: 'nowrap' }}>담당자</span>
                        <div className="premium-scrollbar" style={{
                          flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', overflowY: 'auto', maxHeight: '52px', padding: '2px 0'
                        }}>
                          {assigneesList.length > 0 ? (
                            assigneesList.map((assignee, idx) => (
                              <span key={idx} style={{
                                background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe',
                                padding: '1px 5px', borderRadius: '3px', fontSize: '0.68rem', fontWeight: 600,
                                display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap'
                              }}>
                                {assignee.name} {assignee.position}
                              </span>
                            ))
                          ) : (
                            <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>미지정</span>
                          )}
                        </div>
                      </div>

                      {/* 위험성 감소 지수 표기 (2줄 콤팩트 센터 정렬) */}
                      <div style={{ textAlign: 'center', background: '#f8fafc', padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          위험성 감소 변화
                        </span>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          {/* 개선전 뱃지 (Pop-over 수정 지원) */}
                          <div style={{ position: 'relative', zIndex: (popoverActionId === item.matrixCheckId && popoverType === 'BEFORE') ? 99999 : 1 }}>
                            <span 
                              className="risk-badge-btn"
                              onClick={() => {
                                if (checkCanEdit(ev)) {
                                  setTempFreq(ev.freqBefore || 1);
                                  setTempSev(ev.sevBefore || 1);
                                  const isOpening = !(popoverActionId === item.matrixCheckId && popoverType === 'BEFORE');
                                  setPopoverActionId(isOpening ? item.matrixCheckId : null);
                                  setPopoverType(isOpening ? 'BEFORE' : null);
                                }
                              }}
                              style={{ 
                                background: beforeRiskObj.color, 
                                color: beforeRiskObj.text, 
                                padding: '4px 10px 4px 14px', 
                                borderRadius: '6px', 
                                fontSize: '0.78rem', 
                                fontWeight: 800,
                                cursor: checkCanEdit(ev) ? 'pointer' : 'default',
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                minWidth: '95px',
                                gap: '6px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                transition: 'all 0.2s'
                              }}
                              title={checkCanEdit(ev) ? '개선 전(최초) 위험도를 수정합니다.' : ''}
                            >
                              <span style={{ flex: 1, textAlign: 'center' }}>{beforeRiskObj.label}</span>
                              {checkCanEdit(ev) && <i className="fas fa-pencil-alt" style={{ fontSize: '0.65rem', opacity: 0.85, marginLeft: '2px' }}></i>}
                            </span>
                            
                            {/* 개선전 인라인 팝오버 UI */}
                            {popoverActionId === item.matrixCheckId && popoverType === 'BEFORE' && (
                              <div className="risk-popover-box" style={{
                                position: 'absolute', 
                                top: 'calc(100% + 8px)', 
                                left: '50%', 
                                transform: 'translateX(-50%)',
                                background: '#ffffff', 
                                border: '1px solid #cbd5e1',
                                borderRadius: '12px', 
                                padding: '12px 14px', 
                                zIndex: 99999,
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '4px',
                                minWidth: '255px'
                              }}>
                                <div style={{
                                  margin: '-12px -14px 10px -14px',
                                  padding: '10px 14px',
                                  background: '#f1f5f9',
                                  borderBottom: '1px solid #e2e8f0',
                                  borderRadius: '12px 12px 0 0',
                                  fontSize: '0.76rem',
                                  fontWeight: 800,
                                  color: '#1e293b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ color: '#f59e0b' }}>⚠️</span> 개선 전 위험성 설정
                                  </span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); setPopoverActionId(null); setPopoverType(null); }}
                                    style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '0.85rem', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                                    title="닫기"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '0 2px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'left', fontWeight: 700 }}>빈도(가능성)</span>
                                    <select value={tempFreq} onChange={e => setTempFreq(e.target.value)} style={{ padding: '4px 6px', fontSize: '0.78rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', background: '#f8fafc', fontWeight: 700, color: '#1e293b' }}>
                                      <option value={3}>상 (3점)</option>
                                      <option value={2}>중 (2점)</option>
                                      <option value={1}>하 (1점)</option>
                                    </select>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'left', fontWeight: 700 }}>강도(중대성)</span>
                                    <select value={tempSev} onChange={e => setTempSev(e.target.value)} style={{ padding: '4px 6px', fontSize: '0.78rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', background: '#f8fafc', fontWeight: 700, color: '#1e293b' }}>
                                      <option value={3}>대 (3점)</option>
                                      <option value={2}>중 (2점)</option>
                                      <option value={1}>소 (1점)</option>
                                    </select>
                                  </div>
                                </div>
                                <div style={{
                                  margin: '10px -14px -12px -14px',
                                  padding: '8px 14px',
                                  background: '#f8fafc',
                                  borderTop: '1px solid #e2e8f0',
                                  borderRadius: '0 0 12px 12px',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: '6px'
                                }}>
                                  <button onClick={() => { setPopoverActionId(null); setPopoverType(null); }} style={{ padding: '5px 14px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.72rem', color: '#475569', cursor: 'pointer', fontWeight: 700 }}>취소</button>
                                  <button onClick={() => handleAutoSaveBeforeRisk(item, tempFreq, tempSev)} style={{ padding: '5px 14px', background: '#f59e0b', border: 'none', borderRadius: '6px', fontSize: '0.72rem', color: 'white', cursor: 'pointer', fontWeight: 800, boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)' }}>저장</button>
                                </div>
                              </div>
                            )}
                          </div>

                          <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 'bold' }}><i className="fas fa-arrow-right"></i></span>
                          
                          {/* 개선후 뱃지 (Pop-over 수정 지원) */}
                          <div style={{ position: 'relative', zIndex: (popoverActionId === item.matrixCheckId && popoverType === 'AFTER') ? 99999 : 1 }}>
                            <span 
                              className="risk-badge-btn"
                              onClick={() => {
                                if (checkCanEdit(ev)) {
                                  setTempFreq(ev.freqAfter || ev.freqBefore || 1);
                                  setTempSev(ev.sevAfter || ev.sevBefore || 1);
                                  const isOpening = !(popoverActionId === item.matrixCheckId && popoverType === 'AFTER');
                                  setPopoverActionId(isOpening ? item.matrixCheckId : null);
                                  setPopoverType(isOpening ? 'AFTER' : null);
                                }
                              }}
                              style={{ 
                                background: afterRiskObj.color, 
                                color: afterRiskObj.text, 
                                padding: '4px 10px 4px 14px', 
                                borderRadius: '6px', 
                                fontSize: '0.78rem', 
                                fontWeight: 800,
                                cursor: checkCanEdit(ev) ? 'pointer' : 'default',
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                minWidth: '95px',
                                gap: '6px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                transition: 'all 0.2s'
                              }}
                              title={checkCanEdit(ev) ? '개선 후 위험도를 수정합니다.' : ''}
                            >
                              <span style={{ flex: 1, textAlign: 'center' }}>{afterRiskObj.label}</span>
                              {checkCanEdit(ev) && <i className="fas fa-pencil-alt" style={{ fontSize: '0.65rem', opacity: 0.85, marginLeft: '2px' }}></i>}
                            </span>
                            
                            {/* 개선후 인라인 팝오버 UI */}
                            {popoverActionId === item.matrixCheckId && popoverType === 'AFTER' && (
                              <div className="risk-popover-box" style={{
                                position: 'absolute', 
                                top: 'calc(100% + 8px)', 
                                left: '50%', 
                                transform: 'translateX(-50%)',
                                background: '#ffffff', 
                                border: '1px solid #cbd5e1',
                                borderRadius: '12px', 
                                padding: '12px 14px', 
                                zIndex: 99999,
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '4px',
                                minWidth: '255px'
                              }}>
                                <div style={{
                                  margin: '-12px -14px 10px -14px',
                                  padding: '10px 14px',
                                  background: '#f1f5f9',
                                  borderBottom: '1px solid #e2e8f0',
                                  borderRadius: '12px 12px 0 0',
                                  fontSize: '0.76rem',
                                  fontWeight: 800,
                                  color: '#1e293b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ color: '#3b82f6' }}>✨</span> 개선 후 위험성 설정
                                  </span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); setPopoverActionId(null); setPopoverType(null); }}
                                    style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '0.85rem', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                                    title="닫기"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '0 2px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'left', fontWeight: 700 }}>빈도(가능성)</span>
                                    <select value={tempFreq} onChange={e => setTempFreq(e.target.value)} style={{ padding: '4px 6px', fontSize: '0.78rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', background: '#f8fafc', fontWeight: 700, color: '#1e293b' }}>
                                      <option value={3}>상 (3점)</option>
                                      <option value={2}>중 (2점)</option>
                                      <option value={1}>하 (1점)</option>
                                    </select>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'left', fontWeight: 700 }}>강도(중대성)</span>
                                    <select value={tempSev} onChange={e => setTempSev(e.target.value)} style={{ padding: '4px 6px', fontSize: '0.78rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', background: '#f8fafc', fontWeight: 700, color: '#1e293b' }}>
                                      <option value={3}>대 (3점)</option>
                                      <option value={2}>중 (2점)</option>
                                      <option value={1}>소 (1점)</option>
                                    </select>
                                  </div>
                                </div>
                                <div style={{
                                  margin: '10px -14px -12px -14px',
                                  padding: '8px 14px',
                                  background: '#f8fafc',
                                  borderTop: '1px solid #e2e8f0',
                                  borderRadius: '0 0 12px 12px',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: '6px'
                                }}>
                                  <button onClick={() => { setPopoverActionId(null); setPopoverType(null); }} style={{ padding: '5px 14px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.72rem', color: '#475569', cursor: 'pointer', fontWeight: 700 }}>취소</button>
                                  <button onClick={() => handleAutoSaveAfterRisk(item, tempFreq, tempSev)} style={{ padding: '5px 14px', background: '#2563eb', border: 'none', borderRadius: '6px', fontSize: '0.72rem', color: 'white', cursor: 'pointer', fontWeight: 800, boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)' }}>저장</button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 구분선 */}
                      <div style={{ borderTop: '1px solid #e2e8f0', margin: '4px 0 2px 0' }} />

                      {/* 실행 버튼군 (1줄 통합 리뉴얼) */}
                      <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                        {/* 상세 내역 팝업 오픈 */}
                        <button
                          onClick={() => { setSelectedItem(item); setShowDetailModal(true); }}
                          style={{
                            flex: 1, background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1',
                            borderRadius: '6px', padding: '8px 2px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', transition: 'all 0.2s', whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
                        >
                          <i className="far fa-file-alt" style={{ fontSize: '0.75rem' }}></i> 상세조회
                        </button>

                        {/* 사진 등록 버튼 */}
                        {(() => {
                          const photosList = ev.photos || [];
                          const before = photosList.find(p => String(p.type).toUpperCase() === 'BEFORE');
                          const after = photosList.find(p => String(p.type).toUpperCase() === 'AFTER');
                          const photoCount = (before ? 1 : 0) + (after ? 1 : 0);
                          
                          let btnStyle, iconClass, hoverBg;
                          if (photoCount === 2) {
                            btnStyle = { background: '#10b981', color: '#ffffff', border: '1px solid #059669' };
                            iconClass = "fas fa-check-circle";
                            hoverBg = '#059669';
                          } else if (photoCount === 1) {
                            btnStyle = { background: '#fffbeb', color: '#d97706', border: '1px solid #fcd34d' };
                            iconClass = "fas fa-exclamation-circle";
                            hoverBg = '#fef3c7';
                          } else {
                            btnStyle = { background: '#ffffff', color: '#64748b', border: '1px solid #cbd5e1' };
                            iconClass = "far fa-image";
                            hoverBg = '#f8fafc';
                          }

                          return (
                            <button
                              onClick={() => {
                                setPhotoTarget(item);
                                setBeforePhotoId(before ? before.id : null);
                                setAfterPhotoId(after ? after.id : null);
                                setTempBeforeUrl(before ? before.url : '');
                                setTempAfterUrl(after ? after.url : '');
                                setTempBeforeFile(null);
                                setTempAfterFile(null);
                                setDeletedPhotoIds([]);
                                setShowPhotoModal(true);
                              }}
                              style={{
                                flex: 1, ...btnStyle,
                                borderRadius: '6px', padding: '8px 2px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', transition: 'all 0.2s', whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = btnStyle.background; }}
                            >
                              <i className={iconClass} style={{ fontSize: '0.75rem' }}></i> 증빙사진 ({photoCount}/2)
                            </button>
                          );
                        })()}

                        {/* 완료 승인 버튼 (상태에 따라 비활성 제어) */}
                        {(!ev.actionStatus || ev.actionStatus === 'pending') ? (
                          <button
                            onClick={() => handleStartProgress(item)}
                            disabled={!checkCanEdit(ev)}
                            style={{
                              flex: 1,
                              background: !checkCanEdit(ev) ? '#e2e8f0' : '#fffbeb',
                              color: !checkCanEdit(ev) ? '#94a3b8' : '#d97706',
                              border: !checkCanEdit(ev) ? 'none' : '1px solid #fcd34d',
                              borderRadius: '6px',
                              padding: '8px 2px',
                              fontSize: '0.68rem',
                              fontWeight: 800,
                              height: '32px',
                              cursor: !checkCanEdit(ev) ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (checkCanEdit(ev)) {
                                e.currentTarget.style.background = '#fef3c7';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (checkCanEdit(ev)) {
                                e.currentTarget.style.background = '#fffbeb';
                              }
                            }}
                            title={
                              !checkCanEdit(ev)
                                ? '진행중 처리 권한이 없습니다. (담당자, 관리자만 가능)'
                                : '진행중 상태로 변경합니다.'
                            }
                          >
                            <i className="fas fa-play" style={{ fontSize: '0.75rem' }}></i> 진행중 처리
                          </button>
                        ) : ev.actionStatus === 'in_progress' ? (
                          <button
                            onClick={() => handleApprove(item)}
                            disabled={!checkCanEdit(ev)}
                            style={{
                              flex: 1,
                              background: !checkCanEdit(ev) ? '#e2e8f0' : '#10b981',
                              color: !checkCanEdit(ev) ? '#94a3b8' : '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 2px',
                              fontSize: '0.68rem',
                              fontWeight: 800,
                              height: '32px',
                              cursor: !checkCanEdit(ev) ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (checkCanEdit(ev)) {
                                e.currentTarget.style.background = '#059669';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (checkCanEdit(ev)) {
                                e.currentTarget.style.background = '#10b981';
                              }
                            }}
                            title={
                              !checkCanEdit(ev)
                                ? '최종 완료 승인 권한이 없습니다. (담당자, 관리자만 가능)'
                                : '조치 완료 승인 (결재)'
                            }
                          >
                            <i className="fas fa-check-circle" style={{ fontSize: '0.75rem' }}></i> 조치완료
                          </button>
                        ) : (
                          <button
                            disabled={true}
                            style={{
                              flex: 1,
                              background: '#e2e8f0',
                              color: '#94a3b8',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 2px',
                              fontSize: '0.68rem',
                              fontWeight: 800,
                              height: '32px',
                              cursor: 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap'
                            }}
                            title="이미 조치 완료 승인된 항목입니다."
                          >
                            <i className="fas fa-check-double" style={{ fontSize: '0.75rem' }}></i> 처리완료
                          </button>
                        )}

                      </div>
                    </div>

                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* 페이징 내비게이션 (데이터가 없어도 항상 렌더링) */}
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '24px', paddingBottom: '0px' }}>
          <div style={{
            position: 'absolute', left: '24px',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: totalCount === 0 ? '#f8fafc' : '#eff6ff',
            border: totalCount === 0 ? '1px solid #e2e8f0' : '1px solid #bfdbfe',
            padding: '4px 12px', borderRadius: '20px',
            fontSize: '0.8rem', color: totalCount === 0 ? '#94a3b8' : '#1e3a8a', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
            opacity: totalCount === 0 ? 0.6 : 1
          }}>
            <span style={{ color: totalCount === 0 ? '#94a3b8' : '#2563eb' }}>{totalCount === 0 ? 0 : currentPage}</span>
            <span style={{ color: totalCount === 0 ? '#e2e8f0' : '#93c5fd', margin: '0 -2px' }}>/</span>
            <span>{totalCount === 0 ? 0 : Math.max(1, totalPages)}</span>
            <span style={{ color: totalCount === 0 ? '#e2e8f0' : '#bfdbfe', margin: '0 2px' }}>|</span>
            <span>총 {totalCount}건</span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(() => {
              const PAGE_BLOCK_SIZE = 10;
              const safeTotalPages = Math.max(1, totalPages);
              const startPage = Math.floor((currentPage - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
              const endPage = Math.min(startPage + PAGE_BLOCK_SIZE - 1, safeTotalPages);
              const pagesInBlock = Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);

              return (
                <>
                  {/* 처음으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1 || totalCount === 0}
                    style={{
                      opacity: (currentPage === 1 || totalCount === 0) ? 0.4 : 1,
                      cursor: (currentPage === 1 || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === 1 || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="처음으로"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  {/* 이전 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.max(startPage - 1, 1))}
                    disabled={startPage === 1 || totalCount === 0}
                    style={{
                      opacity: (startPage === 1 || totalCount === 0) ? 0.4 : 1,
                      cursor: (startPage === 1 || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (startPage === 1 || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="이전 10페이지"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {pagesInBlock.map(page => (
                    <button
                      key={page}
                      className={`sf-btn sf-btn-sm ${currentPage === page && totalCount > 0 ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                      onClick={() => setCurrentPage(page)}
                      disabled={totalCount === 0}
                      style={{
                        minWidth: 36, borderRadius: 8,
                        opacity: totalCount === 0 ? 0.5 : 1,
                        cursor: totalCount === 0 ? 'default' : 'pointer',
                        pointerEvents: totalCount === 0 ? 'none' : 'auto'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  {/* 다음 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.min(endPage + 1, safeTotalPages))}
                    disabled={endPage === safeTotalPages || totalCount === 0}
                    style={{
                      opacity: (endPage === safeTotalPages || totalCount === 0) ? 0.4 : 1,
                      cursor: (endPage === safeTotalPages || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (endPage === safeTotalPages || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="다음 10페이지"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                  {/* 마지막으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(safeTotalPages)}
                    disabled={currentPage === safeTotalPages || totalCount === 0}
                    style={{
                      opacity: (currentPage === safeTotalPages || totalCount === 0) ? 0.4 : 1,
                      cursor: (currentPage === safeTotalPages || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === safeTotalPages || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="마지막으로"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </>
              );
            })()}
          </div>
        </div>

      {/* ========================================================================= */}
      {/* 팝업 모달 1: 위험성평가 상세정보 등록서 (RiskViewModal 조회 연동) */}
      {/* ========================================================================= */}
      {showDetailModal && selectedItem && (
        <RiskViewModal
          initialData={selectedItem}
          onClose={() => { setShowDetailModal(false); setSelectedItem(null); }}
          hasPermission={checkCanEdit(selectedItem.evaluation || {})}
          onEdit={(item) => {
            setShowDetailModal(false);
            setSelectedItem(item);
            setShowEditModal(true);
          }}
          onDelete={async (item) => {
            if (!window.confirm('평가 정보를 삭제하시겠습니까?')) return;
            try {
              if (item.evaluation && item.evaluation.id) {
                await deleteAssessEvaluation(item.evaluation.id, token);
              } else {
                await toggleAssessMatrixCheck({
                  assessmentId: item.assessmentId,
                  processId: item.processId,
                  causeId: item.causeId,
                  checked: false
                }, token);
              }
              alert('삭제가 완료되었습니다.');
              setShowDetailModal(false);
              setSelectedItem(null);
              fetchActions();
            } catch (err) {
              console.error(err);
              alert('삭제 실패');
            }
          }}
        />
      )}

      {showEditModal && selectedItem && (
        <RiskFormModal
          id={selectedItem.evaluation?.id}
          assessmentId={selectedItem.assessmentId}
          assessmentName={selectedItem.assessmentName}
          matrixCheckId={selectedItem.matrixCheckId}
          initialData={{
            processId: selectedItem.processId,
            causeId: selectedItem.causeId,
            memo: selectedItem.memo,
            evaluation: selectedItem.evaluation
          }}
          readOnly={!checkCanEdit(selectedItem.evaluation || {})}
          onClose={() => { setShowEditModal(false); setSelectedItem(null); }}
          onSaved={() => { setShowEditModal(false); setSelectedItem(null); fetchActions(); }}
        />
      )}

      {/* ========================================================================= */}
      {/* 팝업 모달 2: 사진 등록 / 보기 모달 (createPortal 적용 및 전면 반응형 UI) */}
      {/* ========================================================================= */}
      {showPhotoModal && photoTarget && (() => {
        const canEditPhotos = checkCanEdit(photoTarget.evaluation || {});
        return createPortal(
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(5px)' }} onClick={() => { setShowPhotoModal(false); setPhotoTarget(null); }}>
            <div style={{ width: '100%', maxWidth: '1180px', maxHeight: '92vh', background: '#fff', borderRadius: '14px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>

              <div style={{ background: '#0f172a', color: '#fff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '1.05rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-camera text-warning"></i>
                  {canEditPhotos ? '현장 증빙 조치 사진 등록/수정' : '현장 증빙 조치 사진 조회'}
                </span>
                <button onClick={() => { setShowPhotoModal(false); setPhotoTarget(null); }} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer' }}><i className="fas fa-times"></i></button>
              </div>

              <div style={{ padding: '20px 24px', display: 'flex', gap: '20px', background: '#f8fafc', overflowY: 'auto', flex: 1 }}>
                {/* 개선 전 사진 카드 */}
                <div style={{ flex: 1, background: '#fff', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '0.88rem', color: '#1e293b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="far fa-image text-primary"></i> 개선 전 현장 사진
                    </span>
                    {canEditPhotos && tempBeforeUrl && (
                      <button
                        onClick={() => handlePhotoRemoveLocal('BEFORE')}
                        style={{
                          border: '1px solid #fca5a5', color: '#ef4444', background: '#fff',
                          borderRadius: '6px', padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
                        }}
                      >
                        <i className="far fa-trash-alt"></i> 삭제
                      </button>
                    )}
                  </div>

                  {tempBeforeUrl ? (
                    <div style={{ position: 'relative', width: '100%', height: '50vh', minHeight: '250px', maxHeight: '450px', background: '#ffffff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                      <img src={tempBeforeUrl} alt="Before" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ) : (
                    <div style={{ height: '50vh', minHeight: '250px', maxHeight: '450px', background: '#f1f5f9', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      {canEditPhotos ? (
                        <>
                          <i className="fas fa-cloud-upload-alt fa-3x mb-3" style={{ color: '#3b82f6' }}></i>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoSelect(e, 'BEFORE')}
                            style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>클릭하거나 이미지를 드래그하세요</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>지원 형식: JPG, PNG 이미지 파일</span>
                        </>
                      ) : (
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>등록된 조치 전 사진 없음</span>
                      )}
                    </div>
                  )}
                </div>

                {/* 개선 후 사진 카드 */}
                <div style={{ flex: 1, background: '#fff', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '0.88rem', color: '#1e293b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="far fa-image text-success"></i> 개선 후 현장 사진
                    </span>
                    {canEditPhotos && tempAfterUrl && (
                      <button
                        onClick={() => handlePhotoRemoveLocal('AFTER')}
                        style={{
                          border: '1px solid #fca5a5', color: '#ef4444', background: '#fff',
                          borderRadius: '6px', padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
                        }}
                      >
                        <i className="far fa-trash-alt"></i> 삭제
                      </button>
                    )}
                  </div>

                  {tempAfterUrl ? (
                    <div style={{ position: 'relative', width: '100%', height: '50vh', minHeight: '250px', maxHeight: '450px', background: '#ffffff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                      <img src={tempAfterUrl} alt="After" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ) : (
                    <div style={{ height: '50vh', minHeight: '250px', maxHeight: '450px', background: '#f1f5f9', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      {canEditPhotos ? (
                        <>
                          <i className="fas fa-cloud-upload-alt fa-3x mb-3" style={{ color: '#10b981' }}></i>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoSelect(e, 'AFTER')}
                            style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>클릭하거나 이미지를 드래그하세요</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>지원 형식: JPG, PNG 이미지 파일</span>
                        </>
                      ) : (
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>등록된 조치 후 사진 없음</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ background: '#ffffff', padding: '14px 24px', display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid #e2e8f0' }}>
                {canEditPhotos && (
                  <button
                    onClick={handlePhotoSaveAll}
                    disabled={uploading}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 24px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', opacity: uploading ? 0.7 : 1 }}
                  >
                    {uploading ? '저장 중...' : '저장'}
                  </button>
                )}
                <button
                  onClick={() => { setShowPhotoModal(false); setPhotoTarget(null); }}
                  style={{ background: '#64748b', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  닫기
                </button>
              </div>

            </div>
          </div>,
          document.body
        );
      })()}

      {/* ========================================================================= */}
      {/* 팝업 모달 3: 개선 대책 등록 모달 (createPortal 적용) */}
      {/* ========================================================================= */}
      {showEditPlanModal && selectedItem && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(5px)' }} onClick={() => { setShowEditPlanModal(false); setSelectedItem(null); }}>
          <div style={{ width: '100%', maxWidth: '520px', background: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>

            <div style={{ background: '#f59e0b', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 800 }}><i className="fas fa-edit me-2"></i>개선(감소)대책 및 이행계획 등록</span>
              <button onClick={() => { setShowEditPlanModal(false); setSelectedItem(null); }} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' }}><i className="fas fa-times"></i></button>
            </div>

            <div style={{ padding: '20px', background: '#f8fafc' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>위험 요인 내역</label>
              <div style={{ background: '#fff', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', color: '#1e293b', marginBottom: '14px' }}>
                {selectedItem.memo || '선택된 위해 요인'}
              </div>

              <label style={{ fontSize: '0.82rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>개선(감소) 대책 및 이행 내용 입력</label>
              <textarea
                value={editPlanText}
                onChange={e => setEditPlanText(e.target.value)}
                rows={4}
                placeholder="현장 조치 계획 및 수립 내용을 구체적으로 입력하세요."
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', resize: 'vertical' }}
              />
            </div>

            <div style={{ background: '#fff', padding: '12px 20px', display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={handleSavePlan}
                style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
              >
                저장
              </button>
              <button
                onClick={() => { setShowEditPlanModal(false); setSelectedItem(null); }}
                style={{ background: '#64748b', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
              >
                취소
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

      </div>
    </div>
  );
}

// 입력 지연 방지 - 유해위험요인 전용 독립 컴포넌트
function InlineMeasureBox({ item, ev, isMeasureEmpty, canEdit, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(ev?.currentMeasure || item?.memo || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(ev?.currentMeasure || item?.memo || '');
  }, [ev?.currentMeasure, item?.memo]);

  const handleSaveClick = async () => {
    setSaving(true);
    await onSave(item, text);
    setSaving(false);
    setIsEditing(false);
  };

  return (
    <div style={{
      flex: 1,
      background: isMeasureEmpty ? '#fff5f5' : '#f8fafc',
      padding: '8px',
      borderRadius: '10px',
      border: isMeasureEmpty ? '1px solid #fde68a' : '1px solid #fecaca',
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px', paddingBottom: '4px', borderBottom: isMeasureEmpty ? '1px solid #fcd34d' : '1px solid #fca5a5' }}>
        <span style={{ color: isMeasureEmpty ? '#b45309' : '#dc2626', fontWeight: 700, fontSize: '0.82rem', display: 'flex', alignItems: 'center' }}>
          <span style={{ background: isMeasureEmpty ? '#fef3c7' : '#fee2e2', padding: '2px 4px', borderRadius: '4px', marginRight: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className={isMeasureEmpty ? "fas fa-exclamation-triangle" : "fas fa-search"} style={{ fontSize: '0.7rem' }}></i>
          </span>
          유해위험요인 파악
        </span>
        {canEdit && (
          isEditing ? (
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={handleSaveClick} disabled={saving} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', opacity: saving ? 0.7 : 1 }}>
                {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>저장 중</> : '저장'}
              </button>
              <button onClick={() => { setIsEditing(false); setText(ev?.currentMeasure || item?.memo || ''); }} disabled={saving} style={{ background: '#94a3b8', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', opacity: saving ? 0.7 : 1 }}>취소</button>
            </div>
          ) : (
            <button onClick={() => setIsEditing(true)} style={{ background: isMeasureEmpty ? '#f59e0b' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              {isMeasureEmpty ? '등록' : '수정'}
            </button>
          )
        )}
      </div>
      <div className="premium-scrollbar" style={{
        height: '108px',
        overflowY: 'auto',
        background: '#fafbfc',
        border: isEditing ? '2px solid #3b82f6' : (isMeasureEmpty ? '1px solid #fde68a' : '1px solid #fecaca'),
        borderRadius: '6px',
        padding: '6px 8px',
        color: isMeasureEmpty ? '#94a3b8' : '#334155',
        fontSize: '0.78rem',
        lineHeight: 1.25,
        whiteSpace: 'pre-wrap'
      }}>
        {isEditing ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', resize: 'none', outline: 'none', fontSize: '0.78rem', color: '#334155' }}
            placeholder="유해위험요인을 입력하세요"
          />
        ) : (
          isMeasureEmpty ? '등록된 유해위험요인이 없습니다.' : (ev?.currentMeasure || item?.memo)
        )}
      </div>
    </div>
  );
}

// 입력 지연 방지 - 개선대책 전용 독립 컴포넌트
function InlineActionBox({ item, ev, isActionPlanEmpty, canEdit, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(ev?.action || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(ev?.action || '');
  }, [ev?.action]);

  const handleSaveClick = async () => {
    setSaving(true);
    await onSave(item, text);
    setSaving(false);
    setIsEditing(false);
  };

  return (
    <div style={{
      flex: 1,
      background: isActionPlanEmpty ? '#fffbeb' : '#f0fdf4',
      padding: '8px',
      borderRadius: '10px',
      border: isActionPlanEmpty ? '1px solid #fde68a' : '1px solid #bbf7d0',
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px', paddingBottom: '4px', borderBottom: isActionPlanEmpty ? '1px solid #fcd34d' : '1px solid #86efac' }}>
        <span style={{ color: isActionPlanEmpty ? '#b45309' : '#15803d', fontWeight: 700, fontSize: '0.82rem', display: 'flex', alignItems: 'center' }}>
          {isActionPlanEmpty ? (
            <>
              <span style={{ background: '#fef3c7', padding: '2px 4px', borderRadius: '4px', marginRight: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '0.7rem' }}></i>
              </span>
              개선 대책 수립 대기중
            </>
          ) : (
            <>
              <span style={{ background: '#dcfce7', padding: '2px 4px', borderRadius: '4px', marginRight: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-check" style={{ fontSize: '0.7rem' }}></i>
              </span>
              개선(감소)대책 수립
            </>
          )}
        </span>
        {canEdit && (
          isEditing ? (
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={handleSaveClick} disabled={saving} style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', opacity: saving ? 0.7 : 1 }}>
                {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '4px' }}></i>저장 중</> : '저장'}
              </button>
              <button onClick={() => { setIsEditing(false); setText(ev?.action || ''); }} disabled={saving} style={{ background: '#94a3b8', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', opacity: saving ? 0.7 : 1 }}>취소</button>
            </div>
          ) : (
            <button onClick={() => setIsEditing(true)} style={{ background: isActionPlanEmpty ? '#f59e0b' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              {isActionPlanEmpty ? '등록' : '수정'}
            </button>
          )
        )}
      </div>
      <div className="premium-scrollbar" style={{
        height: '108px',
        overflowY: 'auto',
        background: '#fafbfc',
        border: isEditing ? '2px solid #3b82f6' : (isActionPlanEmpty ? '1px solid #fde68a' : '1px solid #a7f3d0'),
        borderRadius: '6px',
        padding: '6px 8px',
        color: isActionPlanEmpty ? '#94a3b8' : '#334155',
        fontSize: '0.78rem',
        lineHeight: 1.25,
        whiteSpace: 'pre-wrap'
      }}>
        {isEditing ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', resize: 'none', outline: 'none', fontSize: '0.78rem', color: '#334155' }}
            placeholder="개선대책을 입력하세요"
          />
        ) : (
          isActionPlanEmpty ? '등록된 개선대책이 없습니다.' : ev?.action
        )}
      </div>
    </div>
  );
}

export default SafetyActionList;
